import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Briefcase,
  Globe,
  FileCheck,
  Clock,
  TrendingUp,
  AlertTriangle,
  ChevronRight,
  Lightbulb,
  Shield,
  LayoutGrid,
  Users,
  ArrowRight,
} from "lucide-react";
import { ipDashboardApi } from "../../services/api";
import { getJurisdiction } from "./jurisdictionConfig";

// ─── Types ──────────────────────────────────────────────────────────────────

interface PortfolioData {
  totalCases: number;
  patentCount: number;
  trademarkCount: number;
  copyrightCount: number;
  designCount: number;
  otherCount: number;
  jurisdictions: { code: string; count: number }[];
  pendingApplications: number;
  averageCaseAgeDays: number;
  statusBreakdown: { status: string; count: number; percentage: number }[];
  topClients: { clientId: string; name: string; count: number; jurisdictions: string[] }[];
  upcomingDeadlines: {
    _id: string;
    title: string;
    dueDate: string;
    matterId: string;
    matterTitle: string;
    priority: string;
    daysLeft: number;
  }[];
}

// ─── Constants ──────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  filed: "bg-blue-500",
  pending: "bg-amber-500",
  granted: "bg-emerald-500",
  registered: "bg-emerald-500",
  abandoned: "bg-slate-400",
  expired: "bg-red-400",
  opposed: "bg-purple-500",
};

const STATUS_BADGE_COLORS: Record<string, string> = {
  filed: "bg-blue-100 text-blue-700",
  pending: "bg-amber-100 text-amber-700",
  granted: "bg-emerald-100 text-emerald-700",
  registered: "bg-emerald-100 text-emerald-700",
  abandoned: "bg-slate-100 text-slate-600",
  expired: "bg-red-100 text-red-700",
  opposed: "bg-purple-100 text-purple-700",
};

const IP_TYPE_COLORS: Record<string, { bg: string; label: string }> = {
  patent: { bg: "bg-blue-500", label: "Patents" },
  trademark: { bg: "bg-purple-500", label: "Trademarks" },
  copyright: { bg: "bg-amber-500", label: "Copyrights" },
  industrial_design: { bg: "bg-cyan-500", label: "Industrial Designs" },
  other: { bg: "bg-slate-400", label: "Other" },
};

const PRIORITY_COLORS: Record<string, string> = {
  critical: "text-red-600",
  high: "text-amber-600",
  medium: "text-blue-600",
  low: "text-slate-500",
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatLabel = (s: string) =>
  s.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

const formatDate = (d: string) =>
  new Date(d).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" });

// ─── Component ──────────────────────────────────────────────────────────────

const IPPortfolioDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<PortfolioData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ipDashboardApi.get();
      setData(res.data);
    } catch (err) {
      console.error("Failed to fetch portfolio data:", err);
      // Set empty data for graceful empty state
      setData({
        totalCases: 0,
        patentCount: 0,
        trademarkCount: 0,
        copyrightCount: 0,
        designCount: 0,
        otherCount: 0,
        jurisdictions: [],
        pendingApplications: 0,
        averageCaseAgeDays: 0,
        statusBreakdown: [],
        topClients: [],
        upcomingDeadlines: [],
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F6F8] flex items-center justify-center">
        <div
          className="animate-spin h-8 w-8 rounded-full border-4"
          style={{ borderColor: "#0F5132", borderTopColor: "transparent" }}
        />
      </div>
    );
  }

  const d = data!;
  const hasData = d.totalCases > 0;

  // Compute IP type donut segments
  const ipTypes = [
    { key: "patent", count: d.patentCount },
    { key: "trademark", count: d.trademarkCount },
    { key: "copyright", count: d.copyrightCount },
    { key: "industrial_design", count: d.designCount },
    { key: "other", count: d.otherCount },
  ].filter((t) => t.count > 0);

  const maxJurisdictionCount = Math.max(...(d.jurisdictions.map((j) => j.count) || [1]), 1);

  return (
    <div className="min-h-screen bg-[#F4F6F8]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-[#0F5132] flex items-center justify-center">
                <LayoutGrid className="h-5 w-5 text-white" />
              </div>
              IP Portfolio
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Bird's-eye view of your firm's intellectual property portfolio
            </p>
          </div>
          <button
            onClick={() => navigate("/ip/docket")}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-colors"
            style={{ backgroundColor: "#0F5132" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#146C43")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#0F5132")}
          >
            <Clock className="h-4 w-4" />
            View Docket
          </button>
        </div>

        {!hasData ? (
          /* ─── Empty State ──────────────────────────────────────────────── */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-16 text-center"
          >
            <div className="mx-auto h-16 w-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
              <Lightbulb className="h-8 w-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No IP Cases Yet</h3>
            <p className="text-sm text-slate-500 max-w-md mx-auto">
              Create a matter with Intellectual Property as the practice area to get started.
              Your portfolio analytics will appear here automatically.
            </p>
            <button
              onClick={() => navigate("/matters")}
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-colors"
              style={{ backgroundColor: "#0F5132" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#146C43")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#0F5132")}
            >
              Go to Matters
              <ArrowRight className="h-4 w-4" />
            </button>
          </motion.div>
        ) : (
          <>
            {/* ─── Summary Cards ─────────────────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0 }}
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-slate-500">Total Active Cases</span>
                  <div className="h-8 w-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Briefcase className="h-4 w-4 text-emerald-600" />
                  </div>
                </div>
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-3xl font-bold text-slate-900"
                >
                  {d.totalCases}
                </motion.span>
                <p className="text-xs text-slate-400 mt-1">
                  {d.patentCount} patent{d.patentCount !== 1 ? "s" : ""}
                  {d.trademarkCount > 0 && `, ${d.trademarkCount} trademark${d.trademarkCount !== 1 ? "s" : ""}`}
                  {d.copyrightCount > 0 && `, ${d.copyrightCount} copyright${d.copyrightCount !== 1 ? "s" : ""}`}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-slate-500">Jurisdictions</span>
                  <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Globe className="h-4 w-4 text-blue-600" />
                  </div>
                </div>
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-3xl font-bold text-slate-900"
                >
                  {d.jurisdictions.length}
                </motion.span>
                <div className="flex items-center gap-1.5 mt-1">
                  {d.jurisdictions.slice(0, 6).map((jur) => {
                    const j = getJurisdiction(jur.code);
                    return (
                      <span key={jur.code} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: j.gradient }} title={j.name}>
                        <i className={j.icon} style={{ color: j.iconColor, fontSize: "9px" }} />
                        {j.code}
                      </span>
                    );
                  })}
                  {d.jurisdictions.length > 6 && (
                    <span className="text-xs text-slate-400">+{d.jurisdictions.length - 6}</span>
                  )}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-slate-500">Pending Applications</span>
                  <div className="h-8 w-8 rounded-lg bg-amber-100 flex items-center justify-center">
                    <FileCheck className="h-4 w-4 text-amber-600" />
                  </div>
                </div>
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-3xl font-bold text-slate-900"
                >
                  {d.pendingApplications}
                </motion.span>
                <p className="text-xs text-slate-400 mt-1">Not yet granted/registered</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-slate-500">Average Case Age</span>
                  <div className="h-8 w-8 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Clock className="h-4 w-4 text-purple-600" />
                  </div>
                </div>
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-3xl font-bold text-slate-900"
                >
                  {d.averageCaseAgeDays < 365
                    ? `${d.averageCaseAgeDays}d`
                    : `${(d.averageCaseAgeDays / 365).toFixed(1)}y`}
                </motion.span>
                <p className="text-xs text-slate-400 mt-1">Since filing date</p>
              </motion.div>
            </div>

            {/* ─── Charts Row ────────────────────────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Jurisdiction Breakdown */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
              >
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-5">
                  Jurisdiction Breakdown
                </h3>
                <div className="space-y-3">
                  {d.jurisdictions
                    .sort((a, b) => b.count - a.count)
                    .map((j, idx) => (
                      <motion.div
                        key={j.code}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.25 + idx * 0.04 }}
                        className="flex items-center gap-3"
                      >
                        <span className="inline-flex items-center justify-center w-8">
                          <i className={getJurisdiction(j.code).icon} style={{ color: getJurisdiction(j.code).iconColor, fontSize: "14px" }} />
                        </span>
                        <span className="text-sm font-medium text-slate-700 w-12">{j.code}</span>
                        <div className="flex-1 h-7 bg-slate-100 rounded-lg overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${(j.count / maxJurisdictionCount) * 100}%` }}
                            transition={{ delay: 0.3 + idx * 0.04, duration: 0.5 }}
                            className="h-full rounded-lg flex items-center justify-end pr-2"
                            style={{ backgroundColor: "#198754" }}
                          >
                            {j.count > 0 && (
                              <span className="text-xs font-bold text-white">{j.count}</span>
                            )}
                          </motion.div>
                        </div>
                      </motion.div>
                    ))}
                  {d.jurisdictions.length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-4">No jurisdiction data</p>
                  )}
                </div>
              </motion.div>

              {/* IP Type Breakdown */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
              >
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-5">
                  IP Type Distribution
                </h3>
                {ipTypes.length > 0 ? (
                  <>
                    {/* Visual bar chart */}
                    <div className="flex items-end gap-4 h-40 mb-6 px-4">
                      {ipTypes.map((t, idx) => {
                        const maxCount = Math.max(...ipTypes.map((x) => x.count), 1);
                        const heightPct = (t.count / maxCount) * 100;
                        const config = IP_TYPE_COLORS[t.key] || IP_TYPE_COLORS.other;
                        return (
                          <div key={t.key} className="flex-1 flex flex-col items-center gap-1">
                            <span className="text-sm font-bold text-slate-700">{t.count}</span>
                            <motion.div
                              initial={{ height: 0 }}
                              animate={{ height: `${heightPct}%` }}
                              transition={{ delay: 0.3 + idx * 0.05, duration: 0.6 }}
                              className={`w-full rounded-t-lg ${config.bg}`}
                              style={{ minHeight: 8 }}
                            />
                          </div>
                        );
                      })}
                    </div>
                    {/* Legend */}
                    <div className="flex flex-wrap items-center justify-center gap-4">
                      {ipTypes.map((t) => {
                        const config = IP_TYPE_COLORS[t.key] || IP_TYPE_COLORS.other;
                        return (
                          <div key={t.key} className="flex items-center gap-2">
                            <span className={`h-3 w-3 rounded-full ${config.bg}`} />
                            <span className="text-xs font-medium text-slate-600">{config.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-12">No IP type data</p>
                )}
              </motion.div>
            </div>

            {/* ─── Status Breakdown ──────────────────────────────────────── */}
            {d.statusBreakdown.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8"
              >
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-5">
                  Status Breakdown
                </h3>
                {/* Stacked bar */}
                <div className="h-6 rounded-full overflow-hidden flex mb-4">
                  {d.statusBreakdown
                    .filter((s) => s.count > 0)
                    .map((s) => (
                      <motion.div
                        key={s.status}
                        initial={{ width: 0 }}
                        animate={{ width: `${s.percentage}%` }}
                        transition={{ duration: 0.6, delay: 0.35 }}
                        className={`${STATUS_COLORS[s.status] || "bg-slate-300"}`}
                        title={`${formatLabel(s.status)}: ${s.count}`}
                      />
                    ))}
                </div>
                <div className="flex flex-wrap gap-4">
                  {d.statusBreakdown.map((s) => (
                    <div key={s.status} className="flex items-center gap-2">
                      <span className={`h-3 w-3 rounded-full ${STATUS_COLORS[s.status] || "bg-slate-300"}`} />
                      <span className="text-sm font-medium text-slate-700">
                        {formatLabel(s.status)}
                      </span>
                      <span className="text-sm text-slate-400">
                        {s.count} ({s.percentage.toFixed(0)}%)
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* ─── Bottom Row ────────────────────────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Clients */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
              >
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-5">
                  Top Clients by Portfolio Size
                </h3>
                {d.topClients.length > 0 ? (
                  <div className="space-y-3">
                    {d.topClients.map((c, idx) => (
                      <motion.div
                        key={c.clientId}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 + idx * 0.04 }}
                        className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer"
                        onClick={() => navigate(`/clients/${c.clientId}`)}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="h-9 w-9 rounded-full flex items-center justify-center text-white font-bold text-sm"
                            style={{ backgroundColor: "#198754" }}
                          >
                            {c.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-900">{c.name}</p>
                            <div className="flex items-center gap-1 mt-0.5">
                              {c.jurisdictions.slice(0, 4).map((jCode) => {
                                const j = getJurisdiction(jCode);
                                return (
                                  <span key={jCode} className="inline-flex items-center gap-0.5 text-[10px]" title={j.name}>
                                    <i className={j.icon} style={{ color: j.iconColor, fontSize: "9px" }} />
                                    <span className="font-medium text-slate-500">{j.code}</span>
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-slate-900">{c.count}</span>
                          <span className="text-xs text-slate-400">case{c.count !== 1 ? "s" : ""}</span>
                          <ChevronRight className="h-4 w-4 text-slate-400" />
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-8">No client data yet</p>
                )}
              </motion.div>

              {/* Upcoming Critical Deadlines */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
              >
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">
                    Upcoming Critical Deadlines
                  </h3>
                  <button
                    onClick={() => navigate("/ip/docket")}
                    className="text-xs font-medium text-emerald-700 hover:text-emerald-800 flex items-center gap-1"
                  >
                    View All <ChevronRight className="h-3 w-3" />
                  </button>
                </div>
                {d.upcomingDeadlines.length > 0 ? (
                  <div className="space-y-3">
                    {d.upcomingDeadlines.map((dl, idx) => {
                      const isOverdue = dl.daysLeft < 0;
                      const isDueSoon = dl.daysLeft >= 0 && dl.daysLeft <= 7;
                      return (
                        <motion.div
                          key={dl._id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.45 + idx * 0.04 }}
                          className={`p-3 rounded-xl border transition-colors cursor-pointer hover:shadow-sm ${
                            isOverdue
                              ? "border-red-200 bg-red-50/50"
                              : isDueSoon
                              ? "border-amber-200 bg-amber-50/50"
                              : "border-slate-200 bg-slate-50"
                          }`}
                          onClick={() => navigate(`/matters/${dl.matterId}`)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-slate-900 truncate">
                                {dl.title}
                              </p>
                              <p className="text-xs text-slate-500 truncate mt-0.5">
                                {dl.matterTitle}
                              </p>
                            </div>
                            <div className="text-right ml-3 flex-shrink-0">
                              <p className="text-xs font-medium text-slate-500">
                                {formatDate(dl.dueDate)}
                              </p>
                              <p
                                className={`text-xs font-bold mt-0.5 ${
                                  isOverdue
                                    ? "text-red-600"
                                    : isDueSoon
                                    ? "text-amber-600"
                                    : "text-emerald-600"
                                }`}
                              >
                                {isOverdue
                                  ? `${Math.abs(dl.daysLeft)}d overdue`
                                  : dl.daysLeft === 0
                                  ? "Today"
                                  : `${dl.daysLeft}d left`}
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="mx-auto h-12 w-12 rounded-xl bg-emerald-50 flex items-center justify-center mb-3">
                      <Shield className="h-6 w-6 text-emerald-500" />
                    </div>
                    <p className="text-sm text-slate-500">All clear! No upcoming critical deadlines.</p>
                  </div>
                )}
              </motion.div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default IPPortfolioDashboard;
